<script setup lang="ts">
import { computed, watch } from "vue"
import { useI18n } from "vue-i18n"

import { DateStrip, Skeleton, TimeDrum } from "@/shared/ui"
import type { DateStripItem, TimeDrumGroup, TimeDrumValue } from "@/shared/ui"

import { civilDate } from "../model/dayMath"
import { buildDateStrip } from "../model/rescheduleDays"
import { groupSlotsByLocalTime, instantForLocalTime, localTimeOfInstant } from "../model/slotGroups"
import { useRecordsStore } from "../model/store"

const props = defineProps<{
  masterId: string
  serviceId: string
  tz: string
  date: string
  modelValue: string | null
  currentDate?: string
}>()

const emit = defineEmits<{
  "update:modelValue": [value: string | null]
  "update:date": [value: string]
}>()

const store = useRecordsStore()
const { t, locale } = useI18n()

// --- Date strip ---

const dateStripItems = computed<DateStripItem[]>(() => {
  const cells = buildDateStrip(new Date(), props.tz, 30, props.currentDate ?? props.date)
  return cells.map((c) => ({
    value: c.date,
    isToday: c.isToday,
    isCurrent: c.isCurrent,
  }))
})

const fmtShort = computed(
  () =>
    new Intl.DateTimeFormat(locale.value, {
      timeZone: "UTC",
      weekday: "short",
      day: "numeric",
    }),
)

const fmtLong = computed(
  () =>
    new Intl.DateTimeFormat(locale.value, {
      timeZone: "UTC",
      weekday: "long",
      day: "numeric",
      month: "long",
    }),
)

function formatShort(date: string): string {
  return fmtShort.value.format(new Date(`${date}T00:00:00Z`))
}

function formatLong(date: string): string {
  return fmtLong.value.format(new Date(`${date}T00:00:00Z`))
}

// --- Time drum ---

const slotGroupsComputed = computed<TimeDrumGroup[]>(() =>
  store.availabilityStatus === "ready"
    ? groupSlotsByLocalTime(store.availabilitySlots, props.tz)
    : [],
)

const timeDrumValue = computed<TimeDrumValue | null>(() => {
  if (!props.modelValue) return null
  const local = localTimeOfInstant(props.modelValue, props.tz)
  return { hour: local.hour, minute: local.minute }
})

function handleTimeDrumUpdate(value: TimeDrumValue | null): void {
  if (value === null) {
    emit("update:modelValue", null)
    return
  }
  const instant = instantForLocalTime(store.availabilitySlots, props.tz, value.hour, value.minute)
  emit("update:modelValue", instant ?? null)
}

const isSelectedToday = computed(() => props.date === civilDate(new Date(), props.tz))

function handleRetry(): void {
  void store.loadAvailability(props.masterId, props.serviceId, props.date)
}

// Reset the selected slot immediately on mount and on every key-prop change.
// immediate: true means consumers may receive a null emit on mount —
// they filter it out (see "filter out any null emits from the initial watch" in spec).
watch(
  [() => props.masterId, () => props.serviceId, () => props.date],
  () => {
    emit("update:modelValue", null)
  },
  { immediate: true },
)

// Reload availability only when props actually change (not on initial mount).
// Tests that pre-set store.availabilityStatus rely on this NOT firing on mount.
watch(
  [() => props.masterId, () => props.serviceId, () => props.date],
  ([masterId, serviceId, date]) => {
    if (masterId && serviceId) {
      void store.loadAvailability(masterId, serviceId, date)
    }
  },
)
</script>

<template>
  <!-- Date strip (always visible) -->
  <DateStrip
    :model-value="date"
    :items="dateStripItems"
    :ariaLabel="t('journal.reschedule.dateGroupLabel')"
    :format-short="formatShort"
    :format-long="formatLong"
    :current-label="t('journal.reschedule.currentDay')"
    :today-label="t('journal.reschedule.todayDay')"
    @update:model-value="emit('update:date', $event)"
  />

  <!-- Time area -->
  <div class="mt-3">
    <!-- Idle: master or service not yet selected -->
    <p
      v-if="!masterId || !serviceId"
      data-testid="time-area-idle"
      class="text-sm text-muted-foreground"
    >
      {{ t("journal.dateTimePicker.idleHint") }}
    </p>

    <!-- Loading -->
    <template v-else-if="store.availabilityStatus === 'loading'">
      <Skeleton class="h-48 w-full" />
    </template>

    <!-- Error -->
    <div
      v-else-if="store.availabilityStatus === 'error'"
      data-testid="time-area-error"
      class="flex flex-col items-center gap-2 text-center"
    >
      <p class="text-sm text-destructive">{{ t("journal.slotPicker.error") }}</p>
      <button
        type="button"
        class="text-sm underline"
        data-testid="btn-retry"
        @click="handleRetry"
      >
        {{ t("journal.errors.reloadAction") }}
      </button>
    </div>

    <!-- Empty -->
    <p
      v-else-if="store.availabilityStatus === 'ready' && store.availabilitySlots.length === 0"
      data-testid="time-area-empty"
      class="text-center text-sm text-muted-foreground"
    >
      {{ isSelectedToday ? t("journal.slotPicker.emptyToday") : t("journal.slotPicker.emptyForDate") }}
    </p>

    <!-- TimeDrum -->
    <TimeDrum
      v-else-if="store.availabilityStatus === 'ready'"
      :groups="slotGroupsComputed"
      :model-value="timeDrumValue"
      :ariaLabel="t('journal.dateTimePicker.timeLabel')"
      :hours-label="t('journal.dateTimePicker.hoursLabel')"
      :minutes-label="t('journal.dateTimePicker.minutesLabel')"
      @update:model-value="handleTimeDrumUpdate"
    />
  </div>
</template>
